import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import usePopover from "components/common/usePopover";
import EventService from "service/event/EventService";
import TherapyService from "../service/TherapyService";
import "../styles/therapyPicker.scss";

/**
 * The header's therapy chip, made changeable.
 *
 * It used to be a plain `<span>`. bridge's ORIGINAL patient screen
 * (ops/.../new-bridge/components/PrimaryTherapy.js) has always let a care
 * manager switch therapy from a dropdown, and that is the behaviour this
 * restores on the record header in both apps.
 *
 * The list is fetched ON OPEN, not on mount: the header renders on every
 * patient page and the master list is only needed by someone about to change
 * something. It is cached for the life of the component afterwards.
 *
 * TWO THERAPIES NEED MORE THAN A TAG, and both reproduce bridge's own flow
 * (PrimaryTherapy.onTherapySelection):
 *
 *   Pregnancy      needs an expected delivery date, written through a
 *                  DIFFERENT endpoint before the therapy is saved. No date on
 *                  file -> ask for one; a date already in the PAST -> ask
 *                  again, as PUT; otherwise save straight through.
 *   New born care  requires an existing minor member on the account. Selecting
 *                  it looks the account up and then either saves (with a
 *                  notice) or refuses — bridge's two messages, verbatim, so
 *                  the two screens read the same to the same care manager.
 *
 * Both are matched by TAG and by TEXT: bridge branches on the tags "9" and
 * "10", but nothing in either codebase pins those tags to those names, so the
 * name is checked as well rather than trusting a magic number.
 */

const PREGNANCY_TAG = "9";
const NEWBORN_TAG = "10";
const NEWBORN_TEXT = /new\s*-?\s*born/i;
const PREGNANCY_TEXT = /pregnan/i;

const isNewborn = (t) => !!t && (String(t.tag) === NEWBORN_TAG || NEWBORN_TEXT.test(t.text));
/* Exported: DeliveryDateLine decides by the same rule whether the header owes
   the patient an expected delivery date. */
export const isPregnancy = (t) =>
  !!t && !isNewborn(t) && (String(t.tag) === PREGNANCY_TAG || PREGNANCY_TEXT.test(t.text));

/* bridge's strings, character for character (PrimaryTherapy.js:78-92). Both go
   out on `showError`, as they do there — including the first, which is a
   notice rather than a failure. */
const MSG_NEWBORN_OK = "Assigning new born care for existing minor member";
const MSG_NEWBORN_NONE = "Therapy cannot be changed because minor member does not exist";
const MSG_ADD_FAILED = "Error in adding delivery due date";
const MSG_UPDATE_FAILED = "Error in updating delivery due date";
const MSG_UPDATE_OK = "Delivery due date updated successfully";

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusMonthsISO = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() + n);
  return d.toISOString().slice(0, 10);
};

const Caret = () => (
  <svg viewBox="0 0 10 6" width="10" height="6" fill="none"
    stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 1l4 4 4-4" />
  </svg>
);

const TherapyPicker = ({ patientId, tag, label, onChanged, disabled }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);          // null = not fetched yet
  const [state, setState] = useState("idle");      // idle | loading | error | saving | checking
  /* The delivery-date prompt. null = closed; otherwise { mode, therapy, date }
     where mode is "add" (no date on file) or "update" (the one on file has
     already passed). */
  const [dueDialog, setDueDialog] = useState(null);
  const service = useRef(new TherapyService()).current;
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const close = useCallback(() => setOpen(false), []);
  const { triggerRef, menuRef, menuStyle } = usePopover(open, close);

  useEffect(() => {
    if (!open || list !== null || state === "loading") return;
    setState("loading");
    service.list(
      (rows) => {
        if (!mounted.current) return;
        setList(rows);
        setState(rows.length ? "idle" : "error");
      },
      () => {
        if (!mounted.current) return;
        setList([]);
        setState("error");
      }
    );
  }, [open, list, state, service]);

  /* Opening the date prompt CLOSES the menu. Leaving it up behind a modal looks
     unfinished, and it is genuinely stale — the choice has been made, what is
     outstanding is the date. */
  const askForDate = (next) => {
    setOpen(false);
    setDueDialog(next);
  };

  /* The plain save. Everything below funnels here once its precondition holds. */
  const save = (t) => {
    setState("saving");
    service.change(
      patientId,
      t.tag,
      () => {
        if (!mounted.current) return;
        setState("idle");
        setOpen(false);
        setDueDialog(null);
        /* The parent owns the displayed value — this component never renders a
           therapy it has not been told about, so a failed save cannot leave the
           chip showing something the server did not accept. */
        onChanged(t.tag, t.text);
      },
      () => {
        if (!mounted.current) return;
        setState("idle");
        EventService.emit("showError", {
          message: "Couldn’t change the therapy. Please try again.",
        });
      }
    );
  };

  const pick = (t) => {
    if (String(t.tag) === String(tag)) {
      setOpen(false);
      return;
    }

    /* NEW BORN CARE — bridge's flow exactly: look the account up, then either
       save with a notice or refuse. Both messages are bridge's, verbatim. */
    if (isNewborn(t)) {
      setState("checking");
      service.minorMembers(
        patientId,
        (members) => {
          if (!mounted.current) return;
          setState("idle");
          if (members && members.length > 0) {
            EventService.emit("showError", { message: MSG_NEWBORN_OK });
            save(t);
          } else {
            EventService.emit("showError", { message: MSG_NEWBORN_NONE });
          }
        },
        () => {
          if (!mounted.current) return;
          setState("idle");
          /* A failed lookup is NOT "no minor member" — refusing on a network
             error would tell the care manager something untrue about the
             account. Same message either way would do exactly that. */
          EventService.emit("showError", {
            message: "Couldn’t check the account’s minor members. Please try again.",
          });
        }
      );
      return;
    }

    /* PREGNANCY — needs a delivery date that has not already passed. */
    if (isPregnancy(t)) {
      setState("checking");
      service.deliveryDate(
        patientId,
        (existing) => {
          if (!mounted.current) return;
          setState("idle");
          const iso = String(existing || "").slice(0, 10);
          if (!iso) {
            askForDate({ mode: "add", therapy: t, date: "" });
          } else if (iso < todayISO()) {
            // On file but in the past — bridge re-asks, and PUTs the answer.
            askForDate({ mode: "update", therapy: t, date: iso });
          } else {
            save(t);
          }
        },
        () => {
          if (!mounted.current) return;
          setState("idle");
          // Unreadable date == no date: ask, rather than guess.
          askForDate({ mode: "add", therapy: t, date: "" });
        }
      );
      return;
    }

    save(t);
  };

  /* The date is written FIRST and the therapy only if that succeeded — a
     pregnancy with no due date is the state this whole branch exists to
     prevent. */
  const saveDueDate = () => {
    if (!dueDialog || !dueDialog.date) return;
    const { mode, therapy, date } = dueDialog;
    setState("saving");
    const done = () => {
      if (!mounted.current) return;
      if (mode === "update") {
        EventService.emit("showError", { message: MSG_UPDATE_OK, tone: "success" });
      }
      save(therapy);
    };
    const failed = () => {
      if (!mounted.current) return;
      setState("idle");
      setDueDialog(null);
      EventService.emit("showError", {
        message: mode === "update" ? MSG_UPDATE_FAILED : MSG_ADD_FAILED,
      });
    };
    if (mode === "update") service.updateDeliveryDate(patientId, date, done, failed);
    else service.addDeliveryDate(patientId, date, done, failed);
  };

  /* No therapy set AND no way to set one: render nothing, which is the state
     the header drew before this was changeable. */
  if (!patientId) return null;

  const text = label || "Set therapy";

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={`ph-chip ph-chip-btn${label ? "" : " is-unset"}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled || state === "saving" || state === "checking"}
        title="Change the patient's primary therapy"
        onClick={() => setOpen((o) => !o)}
      >
        {state === "saving" ? "Saving…" : state === "checking" ? "Checking…" : text}
        <Caret />
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="tp-pop"
            style={{ top: menuStyle.top, left: menuStyle.left }}
            role="listbox"
            aria-label="Primary therapy"
          >
            <span className="tp-pop-cap">Primary therapy</span>

            {state === "loading" ? (
              <p className="tp-msg">Loading…</p>
            ) : state === "error" ? (
              <p className="tp-msg">Couldn’t load the therapy list.</p>
            ) : (
              <div className="tp-list">
                {(list || []).map((t) => {
                  const isCurrent = String(t.tag) === String(tag);
                  /* Pregnancy and New born care are NOT greyed. Both are
                     selectable and answer on selection — bridge's flow, where
                     picking New born care is what produces the message. */
                  const needsMore = isNewborn(t) || isPregnancy(t);
                  return (
                    <button
                      key={t.tag}
                      type="button"
                      role="option"
                      aria-selected={isCurrent}
                      className={`tp-opt${isCurrent ? " is-current" : ""}${needsMore ? " is-conditional" : ""}`}
                      onClick={() => pick(t)}
                    >
                      {/* No tick — the CURRENT option is carried by its fill
                          (see .tp-opt.is-current). `aria-selected` above is
                          what a screen reader reads, and it is already set. */}
                      {t.text}
                    </button>
                  );
                })}
              </div>
            )}
          </div>,
          document.body
        )}

      {/* The delivery-date prompt. Portalled and modal, because it gates a
          write — the therapy is not saved until this succeeds. Ported from
          bridge's TherapyInputDialog, which also does baby profiles; only the
          delivery-date half is here, since that is the only part this flow
          needs. */}
      {dueDialog &&
        ReactDOM.createPortal(
          <div className="tp-backdrop" role="presentation" onClick={() => setDueDialog(null)}>
            <div
              className="tp-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Expected delivery due date"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="tp-modal-title">
                {dueDialog.mode === "update"
                  ? "Update expected delivery date"
                  : "Expected delivery date"}
              </h3>
              <p className="tp-modal-sub">
                {dueDialog.mode === "update"
                  ? "The date on file has already passed. Enter the current one to move this patient to Pregnancy."
                  : "Pregnancy needs an expected delivery date. It is saved before the therapy changes."}
              </p>

              <label className="tp-field">
                <span className="tp-field-label">Expected delivery due date*</span>
                <input
                  type="date"
                  className="tp-input"
                  value={dueDialog.date}
                  /* bridge's own bounds: not in the past, and no more than a
                     full term ahead (TherapyInputDialog's min/max). */
                  min={todayISO()}
                  max={plusMonthsISO(9)}
                  /* Read the value BEFORE the updater. React 16 POOLS synthetic
                     events and recycles them synchronously, so a lazy
                     `(d) => ({ ...d, date: e.target.value })` runs after
                     `e.target` has been nulled — "Cannot read properties of
                     null (reading 'value')", and the date never lands. bridge
                     is on React 16 (react-scripts 3.4.1), so this matters even
                     where phr would tolerate it. */
                  onChange={(e) => {
                    const value = e.target.value;
                    setDueDialog((d) => (d ? { ...d, date: value } : d));
                  }}
                />
              </label>

              <div className="tp-modal-foot">
                <button
                  type="button"
                  className="tp-btn"
                  onClick={() => setDueDialog(null)}
                  disabled={state === "saving"}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="tp-btn is-primary"
                  onClick={saveDueDate}
                  /* No date, no save: the whole point of this dialog. */
                  disabled={!dueDialog.date || state === "saving"}
                >
                  {state === "saving" ? "Saving…" : "Save and change therapy"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

TherapyPicker.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Current therapy tag, as a string. "" when none is set. */
  tag: PropTypes.string,
  /** Current therapy's display name. "" when none is set. */
  label: PropTypes.string,
  /** (tag, text) => void — the parent owns the displayed value. */
  onChanged: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default TherapyPicker;
